const pipelinesBasePath = "/pipelines";
const jobsBasePath = "/jobs";
const configurationsBasePath = "/configurations";
const users = "/users";
const authBasePath = "/auth";
const sampleInventoryBasePath = "/sample-inventory/list-files";
const sampleInventoryListFilesPath = "/projects/{projectId}/sample-inventory/list-files";
const softwareSettingsBasePath = "/software-settings";
const teamsBasePath = "/teams";
const projectsBasePath = "/projects";
const meBasePath = "/me";

// Define the API endpoint routes
export const API_ENDPOINTS = {
  // endpoints specific to Authentication
  oidc: `${authBasePath}/oidc`,
  credentials: `${authBasePath}/credentials`,
  token: `${authBasePath}/token`,
  csfrToken: `${authBasePath}/csrf-token`,

  // endpoints specific to Users
  changeCurrentUserPassword: `${users}/me/password`,
  users,

  // endpoints specific to Pipelines
  pipelines: pipelinesBasePath,
  pipelinesId: `${pipelinesBasePath}/{id}`,
  pipelineSteps: `${pipelinesBasePath}/{pipeline_id}/steps`,

  // endpoints specific to Jobs
  jobs: jobsBasePath,
  jobById: `${jobsBasePath}/{job_id}`,
  cancelPendingBatchesByJobId: `${jobsBasePath}/{job_id}/cancel-pending-batches`,
  abortJobById: `${jobsBasePath}/{job_id}/abort`,
  resumeJobById: `${jobsBasePath}/{job_id}/resume`,
  reconfigureJob: `${jobsBasePath}/{job_id}/reconfigure`,
  availableMergeNodePools: `${configurationsBasePath}/merge-node-pools`,
  jobSteps: `${jobsBasePath}/{job_id}/steps`,
  steps: `${jobsBasePath}/{job_id}/steps/{job_pipeline_step_id}`,
  stepBatches: `${jobsBasePath}/{job_id}/steps/{job_pipeline_step_id}/batches`,
  batchLogs: `${jobsBasePath}/{job_id}/steps/{job_pipeline_step_id}/batches/{batch_id}/logs/{session_id}`,
  batchResults: `${jobsBasePath}/{job_id}/steps/{job_pipeline_step_id}/batches/{batch_id}/results/{session_id}`,
  batchArtifactsDownload: `${jobsBasePath}/{job_id}/steps/{job_pipeline_step_id}/batches/{batch_id}/artifacts`,
  batchArtifactsFiles: `${jobsBasePath}/{job_id}/steps/{job_pipeline_step_id}/batches/{batch_id}/artifacts/files`,
  stepResultsDownload: `${jobsBasePath}/{job_id}/steps/{job_pipeline_step_id}/results`,
  stepResultsFiles: `${jobsBasePath}/{job_id}/steps/{job_pipeline_step_id}/results/files`,
  stepBatchSamples: `${jobsBasePath}/{job_id}/steps/{job_pipeline_step_id}/batches/{batch_id}/samples`,

  //endpoints specific to Software Settings
  softwareSettings: softwareSettingsBasePath,
  // `GetListAsync` is the only software-settings endpoint the backend scopes under a project route segment.
  softwareSettingsListFiles: "/projects/{projectId}/software-settings",
  softwareSettingsUpload: `${softwareSettingsBasePath}/{software}/{version}/create/{type}`,
  softwareSettingsCreate: `/projects/{projectId}${softwareSettingsBasePath}/{software}/{version}/create/{type}`,
  softwareSettingsCheckFile: `/projects/{projectId}${softwareSettingsBasePath}/check`,
  softwareSettingsDelete: `/projects/{projectId}${softwareSettingsBasePath}`,
  softwareSettingsDicriminator: `${softwareSettingsBasePath}/discriminator/{discriminator}`,

  //endpoints specific to Software Versions
  softwareVersions: `/software-versions`,
  softwareVersionsVersion: `/software-versions/{version}`,
  /**
   * Legacy read-only listing that returns plain version strings. Still the only
   * versions endpoint on older managers, where `/software-versions` returns 404.
   */
  softwareVersionsLegacyList: `/software-versions/list`,

  //endpoints specific to Fasta
  fastaListFiles: "/projects/{projectId}/fasta/list-files",
  fasta: "/fasta",
  fastaCreate: "/projects/{projectId}/fasta",
  fastaCheckFile: "/projects/{projectId}/fasta/check",
  fastaDelete: "/projects/{projectId}/fasta",

  // endpoints specific to Inventory, which is for rawFiles and instruments.
  rawFiles: sampleInventoryListFilesPath,
  rawFilesBatch: `${sampleInventoryBasePath}/batch/{ids}`,
  rawFilesUpload: "/projects/{projectId}/sample-inventory/upload",
  rawFilesUploadInit: "/projects/{projectId}/sample-inventory/upload/init",
  rawFilesUploadChunk: "/projects/{projectId}/sample-inventory/upload/chunk",
  rawFilesUploadComplete: "/projects/{projectId}/sample-inventory/upload/complete",
  checkFile: "/projects/{projectId}/sample-inventory/check",
  checkFiles: "/projects/{projectId}/sample-inventory/check-batch",
  rawFilesDelete: "/projects/{projectId}/sample-inventory/files",
  rawFilesUsage: "/projects/{projectId}/sample-inventory/usage",
  instruments: `${sampleInventoryBasePath}/instruments`,

  // storage locations resolved by the backend (container on Azure, folder prefix on AWS)
  storageLocations: "/storage-locations",

  // endpoints specific to the workspace (teams, projects and permissions)
  teams: teamsBasePath,
  teamById: `${teamsBasePath}/{teamId}`,
  teamName: `${teamsBasePath}/{teamId}/name`,
  teamMembers: `${teamsBasePath}/{teamId}/members`,
  teamMemberById: `${teamsBasePath}/{teamId}/members/{userId}`,
  teamProjectInventory: `${teamsBasePath}/{teamId}/projects`,
  teamSoftwareVersionConfigurationEffective: `${teamsBasePath}/{teamId}/software-version-configuration/effective`,
  teamSoftwareVersionConfiguration: `${teamsBasePath}/{teamId}/software-version-configuration`,
  teamSoftwareVersionConfigurationVersions: `${teamsBasePath}/{teamId}/software-version-configuration/versions`,
  teamSoftwareVersionConfigurationVersionById: `${teamsBasePath}/{teamId}/software-version-configuration/versions/{versionId}`,
  teamSoftwareVersionConfigurationDefault: `${teamsBasePath}/{teamId}/software-version-configuration/default`,
  teamSoftwareVersionConfigurationDefaultForSoftware: `${teamsBasePath}/{teamId}/software-version-configuration/default/{software}`,
  teamStorageConfiguration: `${teamsBasePath}/{teamId}/storage-configuration`,
  teamStorageConfigurationPaths: `${teamsBasePath}/{teamId}/storage-configuration/paths`,
  teamStorageConfigurationPathById: `${teamsBasePath}/{teamId}/storage-configuration/paths/{pathId}`,
  teamStorageConfigurationPathVisibility: `${teamsBasePath}/{teamId}/storage-configuration/paths/{pathId}/visibility`,
  teamStorageConfigurationPathValue: `${teamsBasePath}/{teamId}/storage-configuration/paths/{pathId}/value`,
  teamStorageConfigurationResultsPath: `${teamsBasePath}/{teamId}/storage-configuration/results-path`,
  teamStorageConfigurationEffective: `${teamsBasePath}/{teamId}/storage-configuration/effective`,
  teamProjectById: `${teamsBasePath}/{teamId}/projects/{projectId}`,
  teamProjectMoveRequests: `${teamsBasePath}/{teamId}/projects/{projectId}/move-requests`,
  teamMoveRequestAccept: `${teamsBasePath}/{teamId}/move-requests/{requestId}/accept`,
  teamMoveRequestReject: `${teamsBasePath}/{teamId}/move-requests/{requestId}/reject`,
  teamMoveRequestWithdraw: `${teamsBasePath}/{teamId}/move-requests/{requestId}/withdraw`,
  teamMoveRequestsPending: `${teamsBasePath}/{teamId}/move-requests/pending`,
  projects: projectsBasePath,
  projectById: `${projectsBasePath}/{projectId}`,
  projectName: `${projectsBasePath}/{projectId}/name`,
  projectMembers: `${projectsBasePath}/{projectId}/members`,
  projectMemberById: `${projectsBasePath}/{projectId}/members/{userId}`,
  projectStorageConfiguration: `${projectsBasePath}/{projectId}/storage-configuration`,
  projectStorageConfigurationPaths: `${projectsBasePath}/{projectId}/storage-configuration/paths`,
  projectStorageConfigurationPathById: `${projectsBasePath}/{projectId}/storage-configuration/paths/{pathId}`,
  projectStorageConfigurationPathVisibility: `${projectsBasePath}/{projectId}/storage-configuration/paths/{pathId}/visibility`,
  projectStorageConfigurationPathValue: `${projectsBasePath}/{projectId}/storage-configuration/paths/{pathId}/value`,
  projectStorageConfigurationResultsPath: `${projectsBasePath}/{projectId}/storage-configuration/results-path`,
  projectStorageConfigurationEffective: `${projectsBasePath}/{projectId}/storage-configuration/effective`,
  projectSoftwareVersionConfigurationEffective: `${projectsBasePath}/{projectId}/software-version-configuration/effective`,
  projectSoftwareVersionConfiguration: `${projectsBasePath}/{projectId}/software-version-configuration`,
  projectSoftwareVersionConfigurationVersions: `${projectsBasePath}/{projectId}/software-version-configuration/versions`,
  projectSoftwareVersionConfigurationVersionById: `${projectsBasePath}/{projectId}/software-version-configuration/versions/{versionId}`,
  projectSoftwareVersionConfigurationDefault: `${projectsBasePath}/{projectId}/software-version-configuration/default`,
  projectSoftwareVersionConfigurationDefaultForSoftware: `${projectsBasePath}/{projectId}/software-version-configuration/default/{software}`,
  myPermissions: `${meBasePath}/permissions`,
  entitlements: "/entitlements",
  storageConfigurationFolders: "/storage-configuration/folders",

  // deployment-level configuration (Admin-only)
  deploymentStorageConfiguration: "/storage-configuration/deployment",
  deploymentStorageConfigurationPaths: "/storage-configuration/deployment/paths",
  deploymentStorageConfigurationPathById: "/storage-configuration/deployment/paths/{pathId}",
  deploymentStorageConfigurationPathValue: "/storage-configuration/deployment/paths/{pathId}/value",
  deploymentStorageConfigurationResultsPath: "/storage-configuration/deployment/results-path",
  deploymentSoftwareVersionConfiguration: "/software-version-configuration/deployment",
  deploymentSoftwareVersionConfigurationVersions: "/software-version-configuration/deployment/versions",
  deploymentSoftwareVersionConfigurationVersionById: "/software-version-configuration/deployment/versions/{versionId}",
  deploymentSoftwareVersionConfigurationDefault: "/software-version-configuration/deployment/default",
  deploymentSoftwareVersionConfigurationDefaultForSoftware: "/software-version-configuration/deployment/default/{software}",

  // websocket endpoints
  jobsHub: "/hubs/jobs",
  logsHub: "/hubs/logs",

  // license endpoint
  license: "/license",
  licenseStatus: "/license/status",

  // results endpoint
  resultsS3Download: "/jobs/{job_id}/results",
  resultsS3Files: "/jobs/{job_id}/results/files",
  // endpoints specific to Libraries
  libraries: "/projects/{projectId}/libraries/list-files",
  librariesUpload: "/projects/{projectId}/libraries/upload",
  librariesUploadInit: "/projects/{projectId}/libraries/upload/init",
  librariesUploadChunk: "/projects/{projectId}/libraries/upload/chunk",
  librariesUploadComplete: "/projects/{projectId}/libraries/upload/complete",
  // endpoints specific to Archives
  archives: "/archives/list-files",
  archivesJobs: "/archives/jobs",
};
